import { AlertTriangle, Building2, CalendarRange, ShieldCheck } from 'lucide-react'
import { Badge, Button, Modal } from '@/components/ui'
import { formatDateTime } from '@/lib/formatters'
import type { ComplianceReportRow } from '@/features/reports/types/compliance-report.types'

function complianceVariant(
  status: ComplianceReportRow['complianceStatus']
): 'success' | 'warning' | 'danger' {
  if (status === 'compliant') return 'success'
  if (status === 'needs_attention') return 'warning'
  return 'danger'
}

function companyStatusVariant(
  status: ComplianceReportRow['companyStatus']
): 'success' | 'neutral' {
  return status === 'active' ? 'success' : 'neutral'
}

function formatStatusLabel(value: string): string {
  return value.replace(/_/g, ' ')
}

function DetailRow({
  label,
  value,
}: {
  label: string
  value: React.ReactNode
}): React.ReactElement {
  return (
    <div className="flex items-start justify-between gap-4 py-2 text-sm">
      <span className="shrink-0 font-medium text-gray-600 dark:text-gray-400">{label}</span>
      <span className="text-right text-gray-900 dark:text-gray-100">{value}</span>
    </div>
  )
}

const metricLabels: Array<[keyof ComplianceReportRow['metrics'], string]> = [
  ['activeMembers', 'Active members'],
  ['managers', 'Managers'],
  ['pendingLeaveRequests', 'Pending leave requests'],
  ['pendingTimeCorrections', 'Pending time corrections'],
  ['pendingPenaltyApprovals', 'Pending penalty approvals'],
  ['pendingBonusApprovals', 'Pending bonus approvals'],
  ['pendingApprovals', 'Pending approvals'],
  ['totalAuditEvents', 'Audit events'],
  ['financialAuditEvents', 'Financial audit events'],
  ['totalPayrollRuns', 'Payroll runs'],
  ['approvedPayrollRuns', 'Approved payroll runs'],
  ['openPayrollRuns', 'Open payroll runs'],
  ['payrollRunsWithErrors', 'Payroll errors'],
  ['totalDocuments', 'Documents'],
  ['archivedDocuments', 'Archived documents'],
  ['companyDocuments', 'Company documents'],
  ['staffDocuments', 'Staff documents'],
  ['activeAttendanceSessions', 'Active attendance sessions'],
  ['onBreakAttendanceSessions', 'On-break sessions'],
  ['lateAttendanceRecords', 'Late records'],
  ['editedAttendanceRecords', 'Edited records'],
]

export interface ComplianceReportDetailModalProps {
  report: ComplianceReportRow | null
  onClose: () => void
}

export function ComplianceReportDetailModal({
  report,
  onClose,
}: ComplianceReportDetailModalProps): React.ReactElement | null {
  if (!report) return null

  return (
    <Modal
      open={!!report}
      onClose={onClose}
      title="Compliance report details"
      className="max-w-3xl"
      footer={
        <Button type="button" variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="space-y-6">
        <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-4 dark:border-gray-700 dark:bg-gray-900/50">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-gray-400" />
                <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {report.companyName}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge variant={companyStatusVariant(report.companyStatus)} className="capitalize">
                  {report.companyStatus}
                </Badge>
                <Badge variant={complianceVariant(report.complianceStatus)} className="capitalize">
                  {formatStatusLabel(report.complianceStatus)}
                </Badge>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {report.companyCode ? `${report.companyCode} • ` : ''}
                {report.companySlug}
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-right dark:border-gray-700 dark:bg-gray-800">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                Compliance score
              </p>
              <p className="mt-1 text-2xl font-semibold text-gray-950 dark:text-gray-100">
                {report.complianceScore}
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <div className="mb-3 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Open issues
              </h3>
            </div>
            {report.openIssues.length > 0 ? (
              <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                {report.openIssues.map((issue) => (
                  <li key={issue} className="rounded-lg bg-amber-50 px-3 py-2 dark:bg-amber-950/20">
                    {issue}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No open issues for this company in the current period.
              </p>
            )}
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <div className="mb-3 flex items-center gap-2">
              <CalendarRange className="h-4 w-4 text-gray-400" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Report period
              </h3>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              <DetailRow
                label="From"
                value={formatDateTime(report.reportPeriod.dateFrom, 'dd MMM yyyy, p')}
              />
              <DetailRow
                label="To"
                value={formatDateTime(report.reportPeriod.dateTo, 'dd MMM yyyy, p')}
              />
              <DetailRow
                label="Last audit"
                value={
                  report.metrics.lastAuditAt
                    ? formatDateTime(report.metrics.lastAuditAt, 'dd MMM yyyy, p')
                    : 'N/A'
                }
              />
              <DetailRow
                label="Last payroll run"
                value={
                  report.metrics.lastPayrollRunAt
                    ? formatDateTime(report.metrics.lastPayrollRunAt, 'dd MMM yyyy, p')
                    : 'N/A'
                }
              />
              <DetailRow
                label="Last payroll approved"
                value={
                  report.metrics.lastPayrollApprovedAt
                    ? formatDateTime(report.metrics.lastPayrollApprovedAt, 'dd MMM yyyy, p')
                    : 'N/A'
                }
              />
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-4 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Metrics</h3>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {metricLabels.map(([key, label]) => (
              <div
                key={key}
                className="rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-3 dark:border-gray-700 dark:bg-gray-900/40"
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
                  {label}
                </p>
                <p className="mt-1 text-base font-semibold text-gray-900 dark:text-gray-100">
                  {report.metrics[key]}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  )
}

ComplianceReportDetailModal.displayName = 'ComplianceReportDetailModal'
